import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { i18n } from '../../i18n/i18n';
import { KidButton, KidCard } from '../kid/KidUI';
import {
  doneItems, isInstalled, readPocketDismissed, readStart, showChecklist, showPocketInvite, START_ITEMS, StartItem,
  StartState, writePocketDismissed, writeStart,
} from '../../utils/gettingStarted';

/**
 * Parents' Home after setup (Jens 2026-10-15): "Get started" — four small steps that show how the
 * app works — and, a week after setup, an invitation to set up pocket money (not asked during the
 * setup itself, where it would be one decision too many).
 */
const t = (key: string) => i18n.t(`getting_started.${key}`);

export const GettingStartedCard: React.FC<{ family: any }> = ({ family }) => {
  const navigate = useNavigate();
  const [state, setState] = useState<StartState>(() => readStart(family.id));
  const [howTo, setHowTo] = useState(false);
  const done = doneItems(state, family, isInstalled());
  if (!showChecklist(state, done)) return null;

  const save = (next: StartState) => { setState(next); writeStart(family.id, next); };
  const tick = (item: StartItem) => save({ ...state, done: Array.from(new Set([...state.done, item])) });
  const go = (item: StartItem, path: string) => { tick(item); navigate(path); };

  const action: Record<StartItem, () => void> = {
    approve: () => navigate('/approve'),
    own_task: () => go('own_task', '/admin?tab=tasks'),
    home_screen: () => setHowTo((v) => !v),
    calendar: () => go('calendar', '/calendar'),
  };

  return (
    <KidCard tone="mint" className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-kid-heading font-extrabold text-xl md:text-2xl">{t('title')}</h2>
          <p className="text-base">
            {t('progress').replace('{{done}}', String(START_ITEMS.filter((i) => done.has(i)).length)).replace('{{all}}', String(START_ITEMS.length))}
          </p>
        </div>
        <button type="button" className="text-sm underline shrink-0 min-h-[44px]" onClick={() => save({ ...state, dismissed: true })}>
          {t('hide')}
        </button>
      </div>
      <ul className="space-y-2">
        {START_ITEMS.map((item) => {
          const isDone = done.has(item);
          return (
            <li key={item}>
              <button type="button" disabled={isDone} onClick={action[item]}
                className={`w-full min-h-[48px] flex items-center gap-3 rounded-kid-tile px-3 py-2 text-left border-2 ${isDone
                  ? 'border-transparent opacity-70'
                  : 'bg-kid-card dark:bg-kid-card-dark border-kid-border dark:border-kid-border-dark'}`}>
                <span aria-hidden className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center font-bold ${isDone ? 'bg-kid-green text-white' : 'border-2 border-kid-border-strong'}`}>
                  {isDone ? '✓' : ''}
                </span>
                <span className={`flex-1 font-bold ${isDone ? 'line-through' : ''}`}>{t(`item_${item}`)}</span>
                {!isDone && <span aria-hidden>›</span>}
              </button>
              {item === 'home_screen' && howTo && !isDone && (
                <div className="mt-2 ml-10 space-y-2 text-base">
                  <p>{t('home_screen_how')}</p>
                  <KidButton size="md" variant="secondary" onClick={() => tick('home_screen')}>{t('home_screen_done')}</KidButton>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </KidCard>
  );
};

export const PocketMoneyInvite: React.FC<{ family: any }> = ({ family }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => readPocketDismissed(family.id));
  if (!showPocketInvite(family, dismissed)) return null;
  return (
    <KidCard tone="peach" className="space-y-3">
      <h2 className="font-kid-heading font-extrabold text-xl md:text-2xl"><span aria-hidden>💰 </span>{t('pocket_title')}</h2>
      <p className="text-base">{t('pocket_text')}</p>
      <div className="flex flex-wrap gap-2">
        <KidButton size="md" onClick={() => navigate('/admin?tab=rewards')}>{t('pocket_setup')}</KidButton>
        <KidButton size="md" variant="secondary" onClick={() => { writePocketDismissed(family.id); setDismissed(true); }}>{t('pocket_no')}</KidButton>
      </div>
    </KidCard>
  );
};
